import { execFile } from 'node:child_process';
import { promisify } from 'node:util';
import type WebSocket from 'ws';
import { getBridgeRoot } from '../../../files/cwd/index.js';
import { sendWsMessage } from '../../../connection/create-ws-bridge.js';
import type { MessageHandlerDeps } from '../../../routing/message-handler-deps.js';
import type { LogFn } from '../../../log.js';
import { capturePreTurnSnapshot, resolveSnapshotRepoRoots } from '../../../git/snapshot/capture.js';
import type { ApiToBridgePromptMessage } from '../../../types/api-to-bridge-messages.js';
import type { SessionWorktreeManager } from '../../../worktrees/manager.js';

const execFileAsync = promisify(execFile);

async function readGitBranch(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await execFileAsync('git', ['branch', '--show-current'], { cwd, maxBuffer: 64 * 1024 });
    const b = stdout.trim();
    return b || null;
  } catch {
    return null;
  }
}

export async function runBridgePromptPreamble(params: {
  getWs: () => WebSocket | null;
  log: LogFn;
  sessionWorktreeManager: SessionWorktreeManager;
  sessionId: string | undefined;
  runId: string | undefined;
  effectiveCwd: string;
}): Promise<void> {
  const { getWs, log, sessionWorktreeManager, sessionId, runId, effectiveCwd } = params;
  const s = getWs();
  const repoCheckoutPaths = await sessionWorktreeManager.ensureRepoCheckoutPathsForSessionAsync(sessionId);
  const repoRoots = await resolveSnapshotRepoRoots({
    worktreePaths: repoCheckoutPaths,
    fallbackCwd: effectiveCwd,
    sessionId: sessionId?.trim() || undefined,
    log,
  });

  if (s && sessionId) {
    const cliGitBranch = await readGitBranch(effectiveCwd);
    const usesWt = sessionWorktreeManager.usesWorktreeSession(sessionId);
    const isolatedSessionParentPath = sessionWorktreeManager.getIsolatedSessionParentPathForSession(sessionId);
    sendWsMessage(s, {
      type: 'session_git_context_report',
      sessionId,
      cliGitBranch,
      agentUsesWorktree: usesWt,
      sessionParent: usesWt ? 'worktrees_root' : 'bridge_root',
      sessionParentPath: usesWt ? isolatedSessionParentPath ?? effectiveCwd : getBridgeRoot(),
    });
  }

  if (s && sessionId && runId) {
    const cap =
      repoRoots.length > 0
        ? await capturePreTurnSnapshot({ runId, repoRoots, agentCwd: effectiveCwd, log })
        : { ok: false as const, error: 'No git repos' };
    sendWsMessage(s, {
      type: 'pre_turn_snapshot_report',
      sessionId,
      turnId: runId,
      captured: cap.ok,
    });
  }
}

export function parseFollowUpFieldsFromPromptMessage(msg: ApiToBridgePromptMessage): {
  followUpCatalogPromptId: string | null;
} {
  const followUpCatalogPromptId =
    typeof msg.followUpCatalogPromptId === 'string' && msg.followUpCatalogPromptId.trim() !== ''
      ? msg.followUpCatalogPromptId.trim()
      : null;
  return { followUpCatalogPromptId };
}
